import type { EventSource } from "@/types/discover";

/**
 * Display-facing capacity state for a public (group-backed) event, derived from
 * the `is_full` / `has_capacity_limit` / `spots_remaining` fields emitted by
 * ingestion or read live from Spond Core.
 *
 * - `full` — at capacity; show a "fully booked" hint.
 * - `limited` — capped with a known number of spots left; show the count.
 * - `open` — no limit, or the remaining count is unknown; show no hint.
 */
export type PublicEventCapacity =
  | { status: "full" }
  | { status: "limited"; spotsRemaining: number }
  | { status: "open" };

/** Capacity as indexed by ingestion, or as read live from Spond Core. */
export type CapacityFields = Pick<
  EventSource,
  "is_full" | "has_capacity_limit" | "spots_remaining"
>;

export function pickCapacityFields(event: EventSource): CapacityFields {
  return {
    is_full: event.is_full,
    has_capacity_limit: event.has_capacity_limit,
    spots_remaining: event.spots_remaining,
  };
}

export function resolvePublicEventCapacity(
  capacity: CapacityFields,
): PublicEventCapacity {
  // `is_full` wins over a lingering spots_remaining — the event cannot be joined.
  if (capacity.is_full) {
    return { status: "full" };
  }

  // Only surface a count when capacity is capped AND the remaining number is
  // known. `spots_remaining` is `null` when there is no count, in which
  // case we show no potentially-misleading hint.
  if (
    capacity.has_capacity_limit &&
    typeof capacity.spots_remaining === "number" &&
    capacity.spots_remaining > 0
  ) {
    return { status: "limited", spotsRemaining: capacity.spots_remaining };
  }

  return { status: "open" };
}
